<template>
  <b-modal
    :id="'ManuallyEnterContentModal' + article.id"
    title="Manually Enter Content"
    size="lg"
    @ok="submitContentItem"
    :ok-disabled="!isFormValid"
    @show="prePopulateURL()"
  >
    <p
      id="modal-title"
      class="font-weight-bold"
    >
      Profiling content
    </p>
    <p>
      We attempt to extract content from the web pages of the URLs you provide and this works
      for most standard HTML web pages.  When we fail to do that, we ask that you add your
      content manually here.
    </p>
    <p>
      The content you provide here is used to insert a title into your creative, a portion of
      the article content is used to populate the body of your creative and of course the
      landing page URL is the destination for your call to action.
    </p>
    <p>
      Importantly, the title and article content are used by our system to generate a content
      profile.  This content profile is used to match your creative content with contextually
      relevant content from the network of publishers you chose earlier.  This ability to match
      two like pieces of content is the core of the SmartMatch advertising platform.
    </p>
    <p>
      Please enter as much content as you can, the more comprehensive your entry is the better
      the content profile will be, which will drive better matches, which in turn will drive
      stronger engagement.
    </p>
    <b-form-group
      id="fieldset-url"
      label="Landing page URL"
      label-for="input-url"
    >
      <b-row class="gutters-xs">
        <b-col cols="12">
          <b-form-input
            v-model="url"
            :state="validURL(url)"
            id="input-url"
            type="url"
            trim
            placeholder="https://"
            pattern="https?://.+"
            disabled
          />
        </b-col>
      </b-row>
    </b-form-group>
    <b-form-group
      id="fieldset-title"
    >
      <template
        slot="label"
        label-for="input-title"
        class="form-label"
      >
        Title
        <span class="form-label-small text-muted">
          {{ title.length }}/{{ titleMaxLength }}
        </span>
      </template>
      <b-form-input
        id="input-title"
        v-model="title"
        :maxlength="titleMaxLength"
        trim
      />
    </b-form-group>
    <b-form-group
      id="fieldset-article"
    >
      <template
        slot="label"
        label-for="textarea-article"
        class="form-label"
      >
        Article Content
        <span class="form-label-small text-muted">
          {{ articleContent.length }}/{{ articleContentLength.max }}
        </span>
      </template>
      <b-form-textarea
        id="textarea-article"
        v-model="articleContent"
        placeholder=""
        :state="this.articleContent !== '' ? isEnteredContentLengthAccepted : null"
        :maxlength="articleContentLength.max"
        rows="6"
        max-rows="6"
      />
      <b-form-invalid-feedback
        v-if="this.trimmedArticleContent.length < this.articleContentLength.min && this.articleContent !== ''"
        id="manually-entered-content-live-feedback"
        data-test="manually-entered-content-invalid-feedback"
      >
        You must enter at least {{ articleContentLength.min }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      id="fieldset-cat"
    >
      <template
        slot="label"
        label-for="textarea-cta"
        class="form-label"
      >
        Call to action <small>(optional)</small>
      </template>
      <b-form-input
        id="textarea-cta"
        v-model="cta"
        placeholder="Read more"
      />
    </b-form-group>
  </b-modal>
</template>
<script>
import { mapActions } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import helper from '@/core/helper';

function initialData() {
  return {
    url: '',
    title: '',
    articleContent: '',
    cta: '',
    titleMaxLength: 254,
    articleContentLength: {
      min: 150,
      max: 30000,
    },
  };
}

export default {
  name: 'ManuallyEnterContentModal',
  props: {
    article: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return initialData();
  },
  methods: {
    ...mapActions('content', [
      contentTypes.PATCH_CONTENT_ITEM,
      contentTypes.FETCH_ARTICLES,
      contentTypes.SET_CONTENT_ITEMS_URLS_CHANGED,
    ]),
    resetData() {
      Object.assign(this.$data, initialData());
    },
    prePopulateURL() {
      this.resetData();
      if (this.article?.url) this.url = this.article?.url;
    },
    validURL(str) {
      return helper.validURL(str);
    },
    async submitContentItem(bvModalEvt) {
      bvModalEvt.preventDefault();
      const {
        url, title, articleContent, cta,
      } = this;
      await this.PATCH_CONTENT_ITEM({
        scrapeItemId: this.article.id, url, title, articleContent, cta,
      });
      this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
      this.FETCH_ARTICLES();
      this.$nextTick(() => {
        this.$bvModal.hide(`ManuallyEnterContentModal${this.article.id}`);
      });
    },
  },
  computed: {
    isFormValid() {
      return this.url !== ''
        && this.validURL(this.url)
        && this.title !== ''
        && this.isEnteredContentLengthAccepted;
    },
    trimmedArticleContent() {
      return this.articleContent.trim();
    },
    isEnteredContentLengthAccepted() {
      return this.trimmedArticleContent !== ''
        && this.trimmedArticleContent.length >= this.articleContentLength.min
        && this.trimmedArticleContent.length <= this.articleContentLength.max;
    },
  },
  mounted() {
    this.prePopulateURL();
  },
};
</script>
<style lang="scss" scoped>
#modal-title {
  font-size: 24px;
}
</style>
